import React, { useState } from "react";
import styled from "styled-components";
import api from "../../services/api";
import identifyErrorMenssage from "../../services/identifyErrorMenssage";
import { store } from "react-notifications-component";
import Modal from "../modal/modalGeneric";
import ButtonV2 from "../button/button_v2";
import PlanilhaDropzone, { formatFileSize } from "../planilha-dropzone";
import IconClose from "../../assets/icons/close.svg";
import { theme } from "../../styles/theme";

const MODELO_URL =
  "https://sudeste-online.s3.us-east-1.amazonaws.com/importacoes/importaragronomos.xlsx";

const ImportDataHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
`;

const ImportDataModalTitle = styled.h2`
  margin: 0;
  text-align: left;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 22px;
  font-weight: 800;
  color: ${theme.colors.gray.Cinza_2};
`;

const ImportDataCloseButton = styled.button`
  all: unset;

  width: 32px;
  height: 32px;
  min-width: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: ${theme.colors.gray.Cinza_12};
  cursor: pointer;

  img {
    width: 14px;
    height: 14px;
  }
`;

const ImportDataSectionLabel = styled.p`
  width: 100%;
  margin: 12px 0;
  text-align: left;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${theme.colors.gray.Cinza_6};
`;

const ImportDataSteps = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
`;

const ImportDataStep = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const ImportDataStepNumber = styled.div`
  width: 20px;
  height: 20px;
  min-width: 20px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;
  background: ${theme.colors.gray.Cinza_2};
  color: ${theme.colors.white.Branco};
  font-size: 11px;
  font-weight: 800;
`;

const ImportDataStepText = styled.div`
  padding-top: 1px;
  text-align: left;
  font-size: 13px;
  line-height: 1.5;
  color: ${theme.colors.gray.Cinza_2};
`;

const ImportDataAttentionBox = styled.div`
  margin: 8px 0 0 30px;
  padding: 10px 14px;

  background: ${theme.colors.gray.Cinza_13};
  border-left: 3px solid ${theme.colors.orange.Laranja_Sudeste};
  border-radius: 4px;

  text-align: left;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${theme.colors.gray.Cinza_6};

  b {
    color: ${theme.colors.gray.Cinza_2};
  }
`;

const Download = styled.label`
  width: auto;
  font-size: 14px;
  text-align: center;
  color: #589bff;
  cursor: pointer;

  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const ImportFooter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid ${theme.colors.gray.Cinza_11};

  @media only screen and (max-width: 640px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const ImportFooterNote = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  max-width: 340px;
  font-size: 12px;
  color: ${theme.colors.gray.Cinza_6};

  @media only screen and (max-width: 640px) {
    max-width: none;
    text-align: center;
  }
`;

const ImportFooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  @media only screen and (max-width: 640px) {
    > * {
      flex: 1;
    }
  }
`;

const notify = (title, message, type, duration = 4000) => {
  store.addNotification({
    title,
    message,
    type,
    insert: "top",
    container: "top-right",
    animationIn: ["animated", "fadeIn"],
    animationOut: ["animated", "fadeOut"],
    dismiss: { duration, onScreen: true },
  });
};

const ImportarAgronomosModal = ({ show, onHide, userEmail }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadModelo = () => {
    window.open(MODELO_URL, "_blank");
  };

  const deleteFile = () => {
    setFile(null);
  };

  const onSelect = (selectedFile) => {
    setFile(selectedFile);
  };

  const onReject = (motivo) => {
    notify("Erro!", motivo, "danger");
  };

  const closeAndReset = () => {
    setFile(null);
    setLoading(false);
    onHide();
  };

  const handleImport = async () => {
    if (!file) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/agronomos/importar", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      notify(
        "Sucesso!",
        `${response.data.message}${userEmail ? ` Assim que válidado chegará uma notificação para o email: ${userEmail}` : ""}`,
        "success",
        7000,
      );

      closeAndReset();
    } catch (e) {
      notify(
        "Erro!",
        identifyErrorMenssage(
          e,
          "ERRO-581 Erro ao importar os dados - Tente novamente, se o erro persistir acione o suporte.",
        ),
        "danger",
      );
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={closeAndReset}
      paddingModalCard="24px"
      width="720px"
      removeHeader
      marginBottom="0"
    >
      <ImportDataHeader>
        <ImportDataModalTitle>Importar Responsáveis Técnicos</ImportDataModalTitle>
        <ImportDataCloseButton type="button" aria-label="Fechar" onClick={closeAndReset}>
          <img src={IconClose} alt="" />
        </ImportDataCloseButton>
      </ImportDataHeader>

      <ImportDataSectionLabel>Como importar responsáveis técnicos</ImportDataSectionLabel>

      <ImportDataSteps>
        <ImportDataStep>
          <ImportDataStepNumber>1</ImportDataStepNumber>
          <ImportDataStepText>
            <Download onClick={downloadModelo}>Baixe o modelo de planilha</Download>
          </ImportDataStepText>
        </ImportDataStep>

        <ImportDataAttentionBox>
          <b>Atenção:</b> preencha todas as colunas obrigatórias (Nome e CPF) e não remova a
          primeira linha da planilha.
        </ImportDataAttentionBox>

        <ImportDataStep>
          <ImportDataStepNumber>2</ImportDataStepNumber>
          <ImportDataStepText>Selecione o arquivo preenchido e clique em Importar.</ImportDataStepText>
        </ImportDataStep>
      </ImportDataSteps>

      <PlanilhaDropzone
        descricao="Envie a planilha de responsáveis técnicos preenchida conforme o modelo baixado acima."
        arquivo={
          file
            ? {
                nome: file.name,
                tamanho: formatFileSize(file.size),
                extensao: file.name.split(".").pop() || "",
              }
            : null
        }
        onSelect={onSelect}
        onRemove={deleteFile}
        onReject={onReject}
      />

      <ImportFooter>
        <ImportFooterNote>Os registros duplicados são ignorados automaticamente.</ImportFooterNote>
        <ImportFooterActions>
          <ButtonV2
            width="120px"
            height="44px"
            value="CANCELAR"
            outline={true}
            color={theme.colors.gray.Cinza_2}
            disabled={false}
            onClick={closeAndReset}
          />
          <ButtonV2
            width="150px"
            height="44px"
            value="IMPORTAR"
            outline={false}
            disabled={!file}
            onClick={handleImport}
            loading={loading}
          />
        </ImportFooterActions>
      </ImportFooter>
    </Modal>
  );
};

export default ImportarAgronomosModal;
